import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLang } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#5B7FFF] focus:shadow-[0_0_15px_rgba(91,127,255,0.15)] transition-colors duration-200";

export default function Contact() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", project_type: "", budget: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.project_type || !form.budget) {
      toast.error(t.contact.toast_select);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success(t.contact.toast_success);
      setForm({ name: "", email: "", project_type: "", budget: "", message: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t.contact.toast_error);
    } finally {
      setLoading(false);
    }
  };

  const CONTACT_INFO = [
    { icon: Mail, key: "eposta", label: t.contact.info_email, value: "info@cdnbilisim.com", href: "mailto:info@cdnbilisim.com" },
    { icon: Phone, key: "telefon", label: t.contact.info_phone, value: "+44 7365 373 821", href: "tel:+447365373821" },
    { icon: MapPin, key: "konum", label: t.contact.info_location, value: t.contact.location_value, href: null },
  ];

  return (
    <section id="iletisim" className="relative py-24 lg:py-32" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
          >
            <span className="glow-badge rounded-full px-4 py-1.5 text-xs font-medium tracking-widest uppercase">
              {t.contact.badge}
            </span>
            <h2 className="mt-6 font-heading font-black tracking-tighter text-white text-3xl md:text-5xl">
              {t.contact.title_pre}
              <span className="text-[#5B7FFF]">{t.contact.title_accent}</span>
            </h2>
            <p className="mt-6 text-slate-400 text-sm md:text-base leading-relaxed max-w-md">{t.contact.desc}</p>

            <div className="mt-10 flex flex-col gap-5">
              {CONTACT_INFO.map((c) => (
                <div key={c.key} className="flex items-center gap-4" data-testid={`contact-info-${c.key}`}>
                  <span className="w-11 h-11 rounded-xl glow-badge flex items-center justify-center shrink-0">
                    <c.icon size={18} />
                  </span>
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider">{c.label}</div>
                    {c.href ? (
                      <a href={c.href} className="text-white hover:text-[#5B7FFF] transition-colors text-sm md:text-base" dir="ltr">
                        {c.value}
                      </a>
                    ) : (
                      <div className="text-white text-sm md:text-base">{c.value}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7 glass-card rounded-2xl p-8 lg:p-10 flex flex-col gap-5"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs text-slate-400 uppercase tracking-wider mb-2">{t.contact.name_label}</label>
                <input
                  required
                  minLength={2}
                  value={form.name}
                  onChange={set("name")}
                  placeholder={t.contact.name_ph}
                  className={inputCls}
                  data-testid="contact-input-name"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-400 uppercase tracking-wider mb-2">{t.contact.email_label}</label>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder={t.contact.email_ph}
                  className={inputCls}
                  data-testid="contact-input-email"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs text-slate-400 uppercase tracking-wider mb-2">{t.contact.type_label}</label>
                <Select key={`type-${lang}`} value={form.project_type} onValueChange={(v) => setForm({ ...form, project_type: v })}>
                  <SelectTrigger className={inputCls + " h-auto"} data-testid="contact-select-project-type">
                    <SelectValue placeholder={t.contact.select_ph} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1B1E25] border-white/10 text-white">
                    {t.contact.type_options.map((opt, i) => (
                      <SelectItem key={opt} value={opt} data-testid={`project-type-option-${i}`}>
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs text-slate-400 uppercase tracking-wider mb-2">{t.contact.budget_label}</label>
                <Select key={`budget-${lang}`} value={form.budget} onValueChange={(v) => setForm({ ...form, budget: v })}>
                  <SelectTrigger className={inputCls + " h-auto"} data-testid="contact-select-budget">
                    <SelectValue placeholder={t.contact.select_ph} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1B1E25] border-white/10 text-white">
                    {t.contact.budget_options.map((opt, i) => (
                      <SelectItem key={opt} value={opt} data-testid={`budget-option-${i}`}>
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="block text-xs text-slate-400 uppercase tracking-wider mb-2">{t.contact.msg_label}</label>
              <textarea
                required
                minLength={5}
                rows={5}
                value={form.message}
                onChange={set("message")}
                placeholder={t.contact.msg_ph}
                className={inputCls + " resize-none"}
                data-testid="contact-input-message"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 font-semibold bg-[#5B7FFF] text-[#15171C] hover:shadow-[0_0_35px_rgba(91,127,255,0.5)] transition-shadow duration-300 disabled:opacity-60"
              data-testid="contact-submit-button"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} className="rtl:-scale-x-100" />}
              {loading ? t.contact.submitting : t.contact.submit}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
